import { Component, OnInit, signal, HostListener } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AppService } from '@app/app.service';
import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ClipboardModule, Clipboard } from '@angular/cdk/clipboard';
import { ShowEmailDialogComponent } from '@app/shared/show-email-dialog/show-email-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MatChipsModule } from '@angular/material/chips';
import { MatExpansionModule } from '@angular/material/expansion';

@Component({
  selector: 'app-truck-view-public',
  imports: [
    CommonModule,
    CurrencyPipe,
    DatePipe,
    MatCardModule,
    MatProgressSpinnerModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    ClipboardModule,
    MatChipsModule,
    MatExpansionModule,
  ],
  templateUrl: './truck-view-public.component.html',
  styleUrl: './truck-view-public.component.scss',
})
export class TruckViewPublicComponent implements OnInit {
  truckId!: number;
  truck = signal<any>(null);
  loading = signal(false);

  isMobile = window.innerWidth < 768;

  @HostListener('window:resize')
  onResize(): void {
    this.isMobile = window.innerWidth < 768;
  }

  constructor(
    private route: ActivatedRoute,
    private appService: AppService,
    private clipboard: Clipboard,
    private dialog: MatDialog
  ) {
    this.truckId = Number(this.route.snapshot.paramMap.get('id'));
  }

  ngOnInit(): void {
    this.getTruck();

    document.body.classList.add('public-truck-page');
  }

  ngOnDestroy(): void {
    document.body.classList.remove('public-truck-page');
  }

  getTruck(): void {
    this.loading.set(true);

    this.appService.get(`trucks/view/${this.truckId}`).subscribe({
      next: (response: any) => {
        this.truck.set(response.data);
        this.loading.set(false);
      },
      error: (error) => {
        console.error(error);
        this.truck.set(null);
        this.loading.set(false);
      },
    });
  }

  formatPhone(phone: string | null | undefined): string {
    if (!phone) return '';

    const digits = phone.replace(/\D/g, '');

    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }

    return phone;
  }

  toFeetAndInches(totalInches: number): string {
    if (!totalInches) return '—';

    const feet = Math.floor(totalInches / 12);
    const inches = totalInches % 12;

    return `${feet}-${inches}`;
  }

  getCarrierType(carrier: any): string {
    const isCarrier = carrier?.is_carrier ?? false;
    const isBroker = carrier?.is_broker ?? false;

    if (isBroker && isCarrier) return 'BROKER & CARRIER';
    if (isBroker) return 'BROKER';
    if (isCarrier) return 'CARRIER';

    return '';
  }

  toggleSaved(): void {

    const originalValue = this.truck().is_saved;

    this.truck.update(current => ({
      ...current,
      is_saved: !current.is_saved,
    }));

    this.appService
      .patch(`saved/trucks/${this.truck().id}/toggle-saved`, {})
      .subscribe({
        next: (res) => {
          this.truck.update(current => ({
            ...current,
            is_saved: res.data.is_saved,
          }));
        },
        error: (err) => {
          this.truck.update(current => ({
            ...current,
            is_saved: originalValue,
          }));

          this.appService.handleError(err);
        },
      });
  }

  copyLink(): void {
    const url = `${window.location.origin}/trucks/view/${this.truckId}`;

    this.clipboard.copy(url);
    this.appService.showSnackBar('Link copied to clipboard.', 'success');
  }

  openMotus(): void {
    const USDOTNumber = this.truck().carrier?.dot_number;
    const url = `https://motus.dot.gov/customer/${USDOTNumber}/account`;
    window.open(url, '_blank');
  }

  showUserEmail(): void {
    this.dialog.open(ShowEmailDialogComponent, {
      width: '400px',
      data: {
        url: `trucks/view/${this.truckId}/email`,
        truck: this.truck(),
      },
    });
  }

  get tags(): string[] {
  const truck = this.truck();

  if (!truck) {
    return [];
  }

  const tagMap: Record<string, string> = {
    tarp: 'Tarp',
    hazmat: 'Hazmat',
    military_base_access: 'Military Base Access',
    twic: 'TWIC',
    emodal: 'eModal',
    outriggers: 'Outriggers',
    beam_trailer: 'Beam Trailer',
    extendable_trailer: 'Extendable Trailer',
    team: 'Team',
  };

  return Object.entries(tagMap)
    .filter(([key]) => truck[key])
    .map(([, label]) => label);
}
  
}